import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Download, ArrowLeft, Loader2, FileText, Layers, Calendar, Image as ImageIcon, Link2 as Link2Icon, Sparkles, AlertTriangle, Share2, RotateCw } from "lucide-react";
import { format } from "date-fns";
import SlideFeedbackBar from "@/components/SlideFeedbackBar";
import ShareLinkDialog from "@/components/ShareLinkDialog";
import RegenerateSlidesModal from "@/components/RegenerateSlidesModal";

const fmtDate = (d) => {
  try { return format(new Date(d), "MMM d, yyyy h:mm a"); }
  catch { return d || "—"; }
};

/**
 * Stripped-down detail view per product spec:
 *   - Title + slide/deck tag + creation timestamp
 *   - Large active-slide preview
 *   - Horizontal thumbnail carousel BELOW the preview (decks only)
 *   - Download deck / Download slide / Download PPTX CTAs
 *   - Nothing else (no Content input, no Template, no Generated content,
 *     no edit/regenerate, no sources panel — moved to the dedicated edit
 *     flow on the Generator page if needed in future)
 */
export default function UserDetailsPage() {
  const { id, type } = useParams();
  const navigate = useNavigate();
  const isDeck = type === "deck";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSlideIdx, setActiveSlideIdx] = useState(0);
  const [imgVersion, setImgVersion] = useState(0);
  // Cached metadata for content/inspiration files referenced by this row.
  // Populated lazily after `data` lands so the first paint doesn't block.
  const [fileMeta, setFileMeta] = useState({});
  const [shareOpen, setShareOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  // Per-slide rows fetched lazily once the deck loads — RegenerateSlidesModal
  // needs them for the slide-level titles + thumbnail context. We don't
  // re-fetch on every poll; only once when the deck transitions to complete.
  const [slideRows, setSlideRows] = useState([]);

  // Cache-busting key derived from the row's persisted `_version`.
  // Backend bumps this every time admin reuploads. We append it to every
  // <img src> so the browser sees a *different* URL after a reupload and
  // re-fetches (instead of serving the cached old PNG). The fallback to
  // `imgVersion` keeps the polling-while-generating path working for
  // fresh generations that haven't been reuploaded yet.
  const cacheKey = (data?._version ?? 0) + imgVersion;

  const loadData = useCallback(async () => {
    try {
      const endpoint = isDeck ? `${API}/decks/${id}` : `${API}/presentations/${id}`;
      const res = await axios.get(endpoint, { withCredentials: true });
      setData(res.data);
    } catch {
      toast.error("Failed to load details");
    } finally {
      setLoading(false);
    }
  }, [id, isDeck]);

  useEffect(() => { loadData(); }, [loadData]);

  // Once we have the row, look up metadata for the content / inspiration
  // files referenced from `inputs`. We need filenames for the labels and
  // `content_type` to tell images from documents.
  useEffect(() => {
    if (!data) return;
    const inp = data.inputs || {};
    const ids = [
      ...(data.content_image_ids || []),
      ...(inp.content_image_ids || []),
      ...(data.inspiration_file_ids || []),
      ...(inp.inspiration_file_ids || []),
      ...(data.file_ids || []),
      ...(inp.file_ids || []),
    ].filter(Boolean);
    const need = ids.filter(id => !fileMeta[id]);
    if (!need.length) return;
    Promise.all(need.map(id =>
      axios.get(`${API}/files/${id}`, { withCredentials: true })
        .then(r => [id, r.data])
        .catch(() => [id, null])
    )).then(pairs => {
      setFileMeta(prev => {
        const next = { ...prev };
        for (const [id, meta] of pairs) if (meta) next[id] = meta;
        return next;
      });
    });
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-poll while still generating
  useEffect(() => {
    if (!data || data.status !== "generating") return undefined;
    const t = setInterval(() => {
      loadData();
      setImgVersion(v => v + 1);   // bust img cache as new thumbs land
    }, 3000);
    return () => clearInterval(t);
  }, [data, loadData]);

  // Fetch per-slide rows once the deck is complete so the regenerate
  // modal can render titles + thumbnails. We only need this for decks;
  // singles don't have a slide_ids array.
  useEffect(() => {
    if (!isDeck || !data || data.status !== "complete") return;
    const ids = data.slide_ids || [];
    if (!ids.length) return;
    if (slideRows.length === ids.length) return;  // already fetched
    Promise.all(ids.map(id =>
      axios.get(`${API}/presentations/${id}`, { withCredentials: true })
        .then(r => r.data)
        .catch(() => null)
    )).then(rows => setSlideRows(rows.filter(Boolean)));
  }, [isDeck, data, slideRows.length]);

  const handleDownloadAll = () => {
    // Let the server's Content-Disposition pick the slugified <title>.pptx.
    const a = document.createElement("a");
    a.href = isDeck ? `${API}/download-deck/${id}` : `${API}/download/${id}`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleDownloadSlide = (slideId, idx) => {
    const a = document.createElement("a");
    a.href = `${API}/download/${slideId}`;
    // Per-slide downloads inside a deck: server still slugifies the slide
    // title so users get a meaningful name. We only need a fallback hint
    // for slides without a title set.
    a.download = `slide_${idx + 1}.pptx`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (!data) {
    return <div className="page-container"><p className="text-gray-500">Not found</p></div>;
  }

  const status = data.status || "complete";
  const slideIds = data.slide_ids || (data.slides || []).map(s => s.id);
  const activeSlideId = isDeck ? (slideIds[activeSlideIdx] || slideIds[0]) : data.id;
  const pageTitle = isDeck
    ? (data.topic || data.inputs?.topic || "Untitled Deck")
    : (data.inputs?.title || data.topic || "Untitled Slide");
  const createdAt = data.created_at;

  return (
    <div className="page-container max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="user-details-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
        <button
          onClick={() => navigate("/history")}
          className="hover:text-gray-600 flex items-center gap-1"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" />History
        </button>
        <span>/</span>
        <span className="text-gray-900">Details</span>
      </div>

      {/* Header — title, tag, timestamp, download */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1
              className="text-xl sm:text-2xl font-bold tracking-tight truncate"
              data-testid="page-title"
              title={pageTitle}
            >
              {pageTitle}
            </h1>
            <span
              className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-0.5 ${
                isDeck
                  ? "text-indigo-700 bg-indigo-50 border border-indigo-200"
                  : "text-gray-600 bg-gray-100 border border-gray-200"
              }`}
              data-testid="type-badge"
            >
              {isDeck ? <><Layers className="w-3 h-3" />Deck</> : <><FileText className="w-3 h-3" />Slide</>}
            </span>
          </div>
          {createdAt && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1.5" data-testid="created-at">
              <Calendar className="w-3 h-3" />{fmtDate(createdAt)}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {status === "complete" && (isDeck || data.file_path) && (
            <>
              {/* Selective regeneration — decks only. Opens a modal
                  where the user can pick slides + give per-slide title
                  + instructions edits. Forks a NEW deck so the original
                  stays intact. Unchanged slides cost nothing. */}
              {isDeck && (data.slide_ids || []).length > 1 && (
                <Button
                  size="sm" variant="outline"
                  onClick={() => setRegenOpen(true)}
                  data-testid="regen-slides-btn"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  <RotateCw className="w-3.5 h-3.5 mr-1" />
                  Regenerate slides
                </Button>
              )}
              <Button
                size="sm" variant="outline"
                onClick={() => setShareOpen(true)}
                data-testid="share-link-btn"
              >
                <Share2 className="w-3.5 h-3.5 mr-1" />
                Share
              </Button>
              <Button
                size="sm"
                onClick={handleDownloadAll}
                className="bg-black hover:bg-gray-800"
                data-testid={isDeck ? "download-deck-btn" : "download-btn"}
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                {isDeck ? "Download deck" : "Download PPTX"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Big slide view */}
      <Card className="border-gray-200 shadow-none overflow-hidden mb-4" data-testid="slide-main-view">
        <div className="aspect-video bg-gray-50 flex items-center justify-center relative">
          {status === "generating" ? (
            <div className="flex flex-col items-center text-amber-600">
              <Loader2 className="w-10 h-10 animate-spin mb-2" />
              <span className="text-sm font-medium">{data.status_detail || "Generating..."}</span>
            </div>
          ) : status === "failed" ? (
            <div className="flex flex-col items-center text-red-500">
              <span className="text-sm font-medium">Generation failed</span>
              {data.status_detail && <span className="text-xs mt-1 max-w-md text-center">{data.status_detail}</span>}
            </div>
          ) : (
            <img
              src={`${API}/thumbnails/${activeSlideId}?v=${cacheKey}`}
              alt={`Slide ${activeSlideIdx + 1}`}
              className="w-full h-full object-contain"
              data-testid="active-slide-image"
              // If a slide thumb fails (e.g., still rendering), hide gracefully.
              // The polling interval re-tries automatically when status flips.
              onError={(e) => { e.target.style.opacity = "0.2"; }}
              onLoad={(e) => { e.target.style.opacity = "1"; }}
            />
          )}

          {isDeck && slideIds.length > 1 && status === "complete" && (
            <>
              <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full bg-black/70 text-white text-xs font-semibold">
                {activeSlideIdx + 1} / {slideIds.length}
              </div>
              <button
                onClick={() => handleDownloadSlide(activeSlideId, activeSlideIdx)}
                className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white/95 text-gray-800 text-xs font-semibold hover:bg-white transition-colors shadow"
                data-testid={`download-slide-${activeSlideIdx}`}
              >
                <Download className="w-3.5 h-3.5" />Download this slide
              </button>
            </>
          )}
        </div>
      </Card>

      {/* Feedback bar.
          • Single slides → per-slide thumbs + optional comment (slide_id == data.id).
          • Decks → DECK-level feedback only (one row per deck, regardless of which
            slide is active). Per-slide ratings on multi-slide were too noisy and
            blocked aggregation; product call: collapse to the deck-as-a-whole. */}
      {status === "complete" && (
        <SlideFeedbackBar slideId={isDeck ? data.id : activeSlideId} isDeck={isDeck} />
      )}

      {/* Horizontal thumbnail carousel — decks only */}
      {isDeck && slideIds.length > 1 && (
        <div
          className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory"
          data-testid="slide-thumbnail-carousel"
        >
          {slideIds.map((sid, i) => (
            <button
              key={sid}
              onClick={() => setActiveSlideIdx(i)}
              className={`flex-shrink-0 w-44 rounded-lg border-2 overflow-hidden transition-all snap-start ${
                activeSlideIdx === i
                  ? "border-teal-500 ring-2 ring-teal-200"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              data-testid={`thumb-${i}`}
            >
              <div className="aspect-video bg-gray-100 relative">
                <img
                  src={`${API}/thumbnails/${sid}?v=${cacheKey}`}
                  alt={`Slide ${i + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.opacity = "0.3"; }}
                />
                <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-bold">
                  {i + 1}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Details panel — read-only summary of inputs used to generate
          this slide / deck. Fields are intentionally display-only here;
          editing happens on the Generator page when re-running. */}
      <DetailsPanel
        data={data}
        isDeck={isDeck}
        fileMeta={fileMeta}
      />

      <ShareLinkDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        item={data ? { ...data, _type: isDeck ? "deck" : "slide" } : null}
      />

      {isDeck && (
        <RegenerateSlidesModal
          open={regenOpen}
          onClose={() => setRegenOpen(false)}
          deck={data}
          slideRows={slideRows}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DetailsPanel — small summary card listing title, content input, files,
// research output (research mode), and template. Files render as chips
// with a Download button that hits /api/files/{id}/download.
// ─────────────────────────────────────────────────────────────────────
function DetailsPanel({ data, isDeck, fileMeta }) {
  if (!data) return null;
  const inputs = data.inputs || {};
  const title = isDeck
    ? (data.topic || inputs.topic || "")
    : (inputs.title || "");
  // Show ONLY user-entered text here. Decks store the user's free-text input
  // under `inputs.content_text`; single slides store it under `inputs.raw_text`.
  // Fall through to those keys ONLY — never to `inputs.content` / `data.content`,
  // because those carry the Opus-generated slide content (research mode), which
  // is NOT something the user typed and should never surface here.
  const contentInput = isDeck
    ? (inputs.content_text || "")
    : (inputs.raw_text || "");

  // Files attached as supporting CONTENT — image content, PDFs, documents.
  // For decks the field is `file_ids` at the top level (and sometimes
  // mirrored under `inputs.file_ids` on legacy rows). For single slides
  // it's `content_image_ids` at the top level.
  const contentFileIds = data.file_ids || inputs.file_ids || data.content_image_ids || inputs.content_image_ids || [];
  // Style/inspiration references — both single slides AND decks support
  // these. For decks the field is `inspiration_file_ids` top-level (or
  // `inputs.inspiration_file_ids` on legacy rows); for slides same.
  const styleRefIds = data.inspiration_file_ids || inputs.inspiration_file_ids || [];
  const sources = inputs.sources || [];
  // Approach indicator. Prefer the explicitly persisted `approach` field
  // (added 2026-05-11) so a HasData outage doesn't silently flip a
  // Research+Design run to "Design only" on the details page. Heuristic
  // fallback for legacy rows that pre-date that field: presence of
  // grounding artefacts (`sources`, `research_provider`, `research_text`).
  const isResearch = (data.approach === "research")
    || (sources.length > 0)
    || !!data.research_provider
    || !!(inputs.research_text || "").trim();
  const approachLabel = isResearch ? "Wokelo research agent" : "Only my content";

  // Research data tier. Today only "Public" data is available (HasData /
  // web search); "Premium" (S&P CapIQ, CrunchBase, etc.) is still locked.
  // Keep the label generic so the moment Premium ships, this surface
  // already renders the right chip without a UI change. Detection rule:
  // research_provider starting with "premium" (future) → Premium; anything
  // else (hasdata*, opus_web_search, etc.) → Public.
  const provider = (data.research_provider || "").toLowerCase();
  const isPremium = provider.startsWith("premium");
  const tierLabel = isPremium ? "Premium data" : "Public data";

  // Research output = the RAW HasData prose that was fed into the AI
  // along with the system prompt. NEVER fall back to slide content /
  // storyline here — those are model output, not research input. If the
  // run pre-dates the `research_text` capture (legacy rows) we just don't
  // show this card, which is honest about what we have.
  const researchOutput = (inputs.research_text || "").trim();

  const downloadFile = (id) => {
    const a = document.createElement("a");
    a.href = `${API}/files/${id}/download`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const FileChip = ({ id }) => {
    const meta = fileMeta[id];
    const filename = meta?.filename || id;
    const isImage = (meta?.content_type || "").startsWith("image/");
    return (
      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 max-w-full" data-testid={`details-file-${id}`}>
        {isImage && meta?.thumbnail_url ? (
          <img src={meta.thumbnail_url} alt={filename} className="w-8 h-8 object-cover rounded flex-shrink-0" />
        ) : isImage ? (
          <ImageIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
        <span className="text-xs text-gray-700 truncate max-w-[180px]" title={filename}>{filename}</span>
        <button
          onClick={() => downloadFile(id)}
          className="text-gray-400 hover:text-gray-900 transition-colors flex-shrink-0 p-0.5"
          title="Download"
          data-testid={`details-file-download-${id}`}
        >
          <Download className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  };

  return (
    <Card className="border-gray-200 shadow-none mt-6 p-5 space-y-5" data-testid="details-panel">
      <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Details</h2>

      <Field label="Approach" testid="details-approach">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 border ${isResearch ? "bg-violet-50 text-violet-800 border-violet-200" : "bg-gray-100 text-gray-700 border-gray-200"}`}>
            {isResearch ? <Sparkles className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
            {approachLabel}
          </span>
          {isResearch && (
            <span
              className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 border ${isPremium ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-teal-50 text-teal-800 border-teal-200"}`}
              data-testid="details-data-tier"
            >
              {tierLabel}
            </span>
          )}
        </div>
      </Field>

      {/* Subtle warning when this run was grounded in PUBLIC data — we
          can't guarantee accuracy and should ask the user to verify. Only
          shown for research+design runs that didn't hit Premium. */}
      {/* External-research accuracy warning removed 2026-06-30 per
          user request — the disclaimer was creating noise on every
          research-mode deck and users felt it undermined trust in
          their own output. The 5% error-rate guidance still lives on
          the admin debug page (`AdminDetailsPage`) for internal QA. */}

      {title && (
        <Field label="Title" testid="details-title">
          <p className="text-sm text-gray-900">{title}</p>
        </Field>
      )}

      {data.template_name && (
        <Field label="Template" testid="details-template">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 bg-gray-100 text-gray-700 border border-gray-200">
            <Sparkles className="w-3 h-3" />{data.template_name}
          </span>
        </Field>
      )}

      {contentInput && (
        <Field label="Content input" testid="details-content-input">
          <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans bg-gray-50 border border-gray-100 rounded-lg p-3 max-h-48 overflow-y-auto">{contentInput}</pre>
        </Field>
      )}

      {contentFileIds.length > 0 && (
        <Field label={`Content files (${contentFileIds.length})`} testid="details-content-files">
          <div className="flex flex-wrap gap-2">
            {contentFileIds.map(id => <FileChip key={id} id={id} />)}
          </div>
        </Field>
      )}

      {styleRefIds.length > 0 && (
        <Field label={`Style reference images (${styleRefIds.length})`} testid="details-style-refs">
          <div className="flex flex-wrap gap-2">
            {styleRefIds.map(id => <FileChip key={id} id={id} />)}
          </div>
        </Field>
      )}

      {researchOutput && (
        <Field label="Research output" testid="details-research-output">
          <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans bg-gray-50 border border-gray-100 rounded-lg p-3 max-h-72 overflow-y-auto">{researchOutput}</pre>
        </Field>
      )}
    </Card>
  );
}

function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{label}</p>
      {children}
    </div>
  );
}
